import React, { useEffect, useRef, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { API, cachedFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function FolderUploadPage({ showNotification }) {
  const [matters, setMatters] = useState([]);
  const [matterSearch, setMatterSearch] = useState("");
  const [selectedMatterId, setSelectedMatterId] = useState(null);
  const [selectedMatterLabel, setSelectedMatterLabel] = useState("");
  const [loadingMatters, setLoadingMatters] = useState(true);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [folderTree, setFolderTree] = useState(null);
  const [totalSize, setTotalSize] = useState(0);

  const [uploading, setUploading] = useState(false);
  const [uploadLog, setUploadLog] = useState([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    cachedFetch("/api/matters")
      .then(data => { setMatters(data.data || []); setLoadingMatters(false); })
      .catch(() => setLoadingMatters(false));
  }, []);

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const paths = files.map(f => f.webkitRelativePath || f.name);
    setSelectedFiles(files);
    setSelectedPaths(paths);
    setTotalSize(files.reduce((sum, f) => sum + f.size, 0));
    setUploadLog([]);
    setUploadComplete(false);
    setUploadStats(null);

    // Build tree for preview
    const tree = {};
    for (const path of paths) {
      const parts = path.replace(/\\/g, "/").split("/");
      let node = tree;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!node[parts[i]]) node[parts[i]] = {};
        node = node[parts[i]];
      }
      if (!node._files) node._files = [];
      node._files.push(parts[parts.length - 1]);
    }
    setFolderTree(tree);
  };

  const renderTree = (node, indent = 0) => {
    const elements = [];
    for (const [key, val] of Object.entries(node)) {
      if (key === "_files") {
        for (const f of val) {
          elements.push(
            React.createElement("div", {
              key: `${indent}-${f}`,
              style: { paddingLeft: indent * 20, fontSize:"12px", color:C.textMuted, padding:"2px 0 2px " + (indent * 20) + "px", display:"flex", alignItems:"center", gap:"6px" }
            }, I.doc, f)
          );
        }
      } else {
        elements.push(
          React.createElement("div", {
            key: `${indent}-${key}`,
            style: { paddingLeft: indent * 20, fontSize:"13px", fontWeight:600, color:C.navy, padding:"3px 0 3px " + (indent * 20) + "px", display:"flex", alignItems:"center", gap:"6px" }
          }, I.folder, key)
        );
        elements.push(...renderTree(val, indent + 1));
      }
    }
    return elements;
  };

  const handleUpload = async () => {
    if (!selectedMatterId || selectedFiles.length === 0) return;
    if (!window.confirm(`Upload ${selectedFiles.length} file(s) to this matter in Clio?`)) return;
    setUploading(true);
    setUploadLog([]);
    setUploadComplete(false);
    setUploadStats(null);

    const addLog = (msg, type) => setUploadLog(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    addLog(`Starting upload of ${selectedFiles.length} files to matter...`, "info");

    const formData = new FormData();
    for (const file of selectedFiles) {
      formData.append("files", file);
    }
    formData.append("paths", JSON.stringify(selectedPaths));

    try {
      const res = await fetch(`${API}/api/matters/${selectedMatterId}/upload-folder`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || `Upload failed: ${res.status}`);
      }

      // Read SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "folder_created") {
              addLog(`Created folder: ${event.name}`, "success");
            } else if (event.type === "folder_skipped") {
              addLog(`Skipped folder: ${event.name} (${event.reason})`, "info");
            } else if (event.type === "file_uploaded") {
              addLog(`Uploaded: ${event.filename}`, "success");
            } else if (event.type === "error") {
              addLog(event.message, "error");
            } else if (event.type === "complete") {
              setUploadStats(event);
              setUploadComplete(true);
              const msg = `Upload complete: ${event.folders_created} folders, ${event.files_uploaded} files` +
                (event.errors > 0 ? `, ${event.errors} errors` : "");
              addLog(msg, event.errors > 0 ? "warning" : "success");
              showNotification(msg, event.errors > 0 ? "warning" : "success");
            }
          } catch (e) { /* skip malformed lines */ }
        }
      }
    } catch (err) {
      addLog(`Error: ${err.message}`, "error");
      showNotification(err.message, "error");
    }

    setUploading(false);
  };

  const filteredMatters = matters.filter(m => {
    if (!matterSearch) return true;
    const q = matterSearch.toLowerCase();
    return (m.client?.name || "").toLowerCase().includes(q)
      || (m.display_number || "").toLowerCase().includes(q);
  });

  const logColors = { info: C.textMuted, success: "#4ADE80", error: "#EF4444", warning: C.gold };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"900px" } },
    React.createElement("h1", {
      style: { fontFamily:"'Playfair Display',serif", fontSize:"26px", fontWeight:600, color:C.navy, marginBottom:"8px" }
    }, "Upload Folder to Clio"),
    React.createElement("p", {
      style: { fontSize:"14px", color:C.textMuted, marginBottom:"28px" }
    }, "Upload an entire folder of documents to a Clio matter, preserving the folder structure."),

    // Step 1: Select Matter
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"16px" }
      }, "1. Select Matter"),
      React.createElement("input", {
        type: "text",
        placeholder: "Search matters by client name or number...",
        value: matterSearch,
        onChange: e => setMatterSearch(e.target.value),
        style: {
          width:"100%", padding:"10px 14px", borderRadius:"8px",
          border:`1px solid ${C.warmGray}`, fontSize:"14px", marginBottom:"12px",
        }
      }),
      selectedMatterId && React.createElement("div", {
        style: { padding:"10px 14px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30`, marginBottom:"12px", display:"flex", alignItems:"center", gap:"8px" }
      },
        React.createElement("span", { style: { color:C.gold } }, I.check),
        React.createElement("span", { style: { fontSize:"14px", fontWeight:600, color:C.navy } }, selectedMatterLabel),
        React.createElement("button", {
          onClick: () => { setSelectedMatterId(null); setSelectedMatterLabel(""); },
          style: { marginLeft:"auto", border:"none", background:"none", cursor:"pointer", color:C.textLight, fontSize:"14px" }
        }, "\u00d7"),
      ),
      !selectedMatterId && React.createElement("div", {
        style: { maxHeight:"200px", overflowY:"auto" }
      },
        loadingMatters
          ? React.createElement("div", { style: { textAlign:"center", padding:"16px" } }, React.createElement(Spinner, null))
          : filteredMatters.slice(0, 10).map(m =>
              React.createElement("button", {
                key: m.id,
                onClick: () => {
                  setSelectedMatterId(m.id);
                  setSelectedMatterLabel(`${m.client?.name || "Client"} \u2014 ${m.display_number || m.id}`);
                },
                style: {
                  width:"100%", padding:"10px 14px", borderRadius:"8px",
                  border:`1px solid ${C.warmGray}`, background:"#fff",
                  cursor:"pointer", textAlign:"left", marginBottom:"6px",
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                }
              },
                React.createElement("span", { style: { fontSize:"14px", fontWeight:500, color:C.navy } }, m.client?.name || "Client"),
                React.createElement("span", { style: { fontSize:"12px", color:C.textMuted } }, m.display_number || ""),
              )
            ),
      ),
    ),

    // Step 2: Select Folder
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px", opacity: selectedMatterId ? 1 : 0.5 }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"16px" }
      }, "2. Select Folder"),
      React.createElement("input", {
        ref: fileInputRef,
        type: "file",
        // @ts-ignore — webkitdirectory is non-standard but widely supported
        webkitdirectory: "true",
        directory: "true",
        multiple: true,
        onChange: handleFolderSelect,
        style: { display:"none" },
      }),
      React.createElement("button", {
        onClick: () => fileInputRef.current && fileInputRef.current.click(),
        disabled: !selectedMatterId,
        style: {
          padding:"14px 24px", borderRadius:"10px",
          border:`2px dashed ${selectedMatterId ? C.navy : C.warmGray}`,
          background: selectedMatterId ? "transparent" : C.warmWhite,
          color: selectedMatterId ? C.navy : C.textLight,
          fontSize:"14px", fontWeight:600, cursor: selectedMatterId ? "pointer" : "default",
          display:"flex", alignItems:"center", gap:"8px", width:"100%", justifyContent:"center",
        }
      }, I.upload, selectedFiles.length > 0 ? "Choose Different Folder" : "Browse for Folder"),

      // Tree preview
      folderTree && React.createElement("div", {
        style: { marginTop:"16px", border:`1px solid ${C.warmGray}`, borderRadius:"8px", padding:"14px 16px", maxHeight:"250px", overflowY:"auto", background:C.warmWhite }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", marginBottom:"10px" } },
          React.createElement("span", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
            `${selectedFiles.length} files`
          ),
          React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } }, formatSize(totalSize)),
        ),
        ...renderTree(folderTree),
      ),
    ),

    // Step 3: Upload
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px", opacity: selectedFiles.length > 0 ? 1 : 0.5 }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"16px" }
      }, "3. Upload"),
      totalSize > 100 * 1024 * 1024 && React.createElement("div", {
        style: { padding:"10px 14px", borderRadius:"8px", background:C.errorBg, color:C.error, fontSize:"13px", marginBottom:"12px" }
      }, "Total size exceeds 100MB limit. Please select a smaller folder."),
      React.createElement("button", {
        onClick: handleUpload,
        disabled: !selectedMatterId || selectedFiles.length === 0 || uploading || totalSize > 100 * 1024 * 1024,
        style: {
          width:"100%", padding:"14px", borderRadius:"10px", border:"none",
          background: (!selectedMatterId || selectedFiles.length === 0) ? C.warmGray : uploading ? C.navyLight : C.navy,
          color: (!selectedMatterId || selectedFiles.length === 0) ? C.textLight : "#fff",
          fontSize:"14px", fontWeight:600, cursor: uploading ? "default" : "pointer",
          display:"flex", alignItems:"center", justifyContent:"center", gap:"8px",
        }
      },
        uploading
          ? React.createElement(React.Fragment, null, React.createElement(Spinner, {size:16,color:"#fff"}), " Uploading...")
          : uploadComplete
            ? React.createElement(React.Fragment, null, I.check, " Upload Complete")
            : React.createElement(React.Fragment, null, I.upload, ` Upload ${selectedFiles.length} Files to Clio`),
      ),

      // Progress log
      uploadLog.length > 0 && React.createElement("div", {
        style: { marginTop:"16px", background:C.navyDark, borderRadius:"8px", padding:"14px 16px", maxHeight:"300px", overflowY:"auto", fontFamily:"monospace" }
      },
        uploadLog.map((entry, i) =>
          React.createElement("div", {
            key: i,
            style: { fontSize:"12px", padding:"3px 0", color: logColors[entry.type] || C.textMuted, display:"flex", gap:"8px" }
          },
            React.createElement("span", { style: { color:"rgba(255,255,255,0.3)", flexShrink:0 } }, entry.time),
            React.createElement("span", null, entry.msg),
          )
        ),
      ),

      // Summary card
      uploadComplete && uploadStats && React.createElement("div", {
        className: "fade-in",
        style: {
          marginTop:"16px", padding:"16px 20px", borderRadius:"10px",
          background: uploadStats.errors > 0 ? `${C.gold}08` : C.successBg,
          border: `1px solid ${uploadStats.errors > 0 ? C.gold : C.success}30`,
          display:"flex", gap:"24px", alignItems:"center",
        }
      },
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize:"24px", fontWeight:700, color:C.navy } }, uploadStats.folders_created),
          React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, textTransform:"uppercase" } }, "Folders"),
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize:"24px", fontWeight:700, color:C.navy } }, uploadStats.files_uploaded),
          React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, textTransform:"uppercase" } }, "Files"),
        ),
        uploadStats.errors > 0 && React.createElement("div", null,
          React.createElement("div", { style: { fontSize:"24px", fontWeight:700, color:C.error } }, uploadStats.errors),
          React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, textTransform:"uppercase" } }, "Errors"),
        ),
      ),
    ),
  );
}


